import { makeHumanReadable, tableDate } from "@utils";

import { toNumber } from "./numbers";
import type { TableV2Align, TableV2ColumnMeta, TableV2TotalFn } from "./types";

const isEmptyValue = (value: unknown) =>
  value === null || value === undefined || value === "";

// "IN_NETWORK" → "In Network"; lists are joined with commas
export const toReadable = (value: unknown) =>
  (Array.isArray(value) ? value : [value])
    .filter((item) => !isEmptyValue(item))
    .map((item) => makeHumanReadable(String(item)))
    .join(", ");

const YES = new Set(["true", "1", "yes", "y"]);
const NO = new Set(["false", "0", "no", "n"]);

/** Booleans, 1 / 0, and "true" / "yes" style strings (any case). Anything else passes through so bad data stays visible */
export const toYesNo = (value: unknown) => {
  const key = String(value).trim().toLowerCase();
  if (YES.has(key)) return "Yes";
  if (NO.has(key)) return "No";
  return String(value);
};

export const getColumnAlign = (meta?: TableV2ColumnMeta): TableV2Align => {
  if (meta?.align) return meta.align;
  switch (meta?.type) {
    case "number":
    case "currency":
    case "percent":
      return "right";
    case "actions":
      return "right";
    default:
      return "left";
  }
};

const formatNumber = (n: number, meta: TableV2ColumnMeta): string => {
  const { type, decimals, abbreviate, negative } = meta;
  const absolute = negative?.parentheses ? Math.abs(n) : n;
  const options: Intl.NumberFormatOptions =
    type === "currency"
      ? {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: decimals ?? (abbreviate ? 1 : 2),
          minimumFractionDigits: decimals ?? (abbreviate ? 0 : 2),
        }
      : {
          maximumFractionDigits:
            decimals ?? (type === "percent" || abbreviate ? 1 : 2),
          minimumFractionDigits: decimals ?? 0,
        };
  if (abbreviate) options.notation = "compact";
  const text = absolute.toLocaleString("en-US", options);
  const withUnit = type === "percent" ? `${text}%` : text;
  return negative?.parentheses && n < 0 ? `(${withUnit})` : withUnit;
};

export const isNegativeValue = (value: unknown) => {
  const n = toNumber(value);
  return n !== null && n < 0;
};

/** Formats a raw cell value using the column's type preset. Percent values are whole numbers (45.2 → "45.2%"). */
export const formatCellValue = (
  value: unknown,
  meta?: TableV2ColumnMeta,
): string | null => {
  if (isEmptyValue(value)) return null;

  switch (meta?.type) {
    case "number":
    case "currency":
    case "percent": {
      const n = toNumber(value);
      return n === null ? String(value) : formatNumber(n, meta);
    }
    case "date":
    case "datetime":
      return tableDate(String(value), meta.type === "datetime") || null;
    case "readable":
      return toReadable(value) || null;
    case "boolean":
      return toYesNo(value);
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
};

export const computeTotal = (
  values: unknown[],
  fn: TableV2TotalFn,
): number | null => {
  if (fn === "count")
    return values.filter((value) => !isEmptyValue(value)).length;
  const numbers = values
    .filter((value) => !isEmptyValue(value))
    .map(toNumber)
    .filter((n): n is number => n !== null);
  if (numbers.length === 0) return null;
  switch (fn) {
    case "sum":
      return numbers.reduce((total, n) => total + n, 0);
    case "avg":
      return numbers.reduce((total, n) => total + n, 0) / numbers.length;
    case "min":
      return numbers.reduce((min, n) => (n < min ? n : min));
    case "max":
      return numbers.reduce((max, n) => (n > max ? n : max));
  }
};
